import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { AppGlobals } from '@app/app.global';
import { 
  HyperpureOrder, 
  OrdersResponse, 
  OrderResponse, 
  InvoiceUrlResponse 
} from '@modules/hyperpure/interfaces';

@Injectable({
  providedIn: 'root'
})
export class HyperpureOrderService {

  constructor(private http: HttpClient, private appGlobals: AppGlobals) { }

  private entityHeaders(entityId?: string) {
    return entityId ? { 'x-entity-ids': entityId } : {};
  }

  getOrders(params: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    status?: string;
    buyer_account_id?: number;
    buyer_outlet_id?: number;
    entityId?: string;
  }): Observable<OrdersResponse> {
    let httpParams = new HttpParams();
    
    // Extract entityId for headers
    const entityId = params.entityId;
    
    Object.keys(params).forEach(key => {
      // Skip entityId as it goes in header, not query params
      if (key === 'entityId') return;
      
      const value = params[key as keyof typeof params];
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, value.toString());
      }
    });

    return this.http.get<OrdersResponse>(
      `${this.appGlobals.apiUrl.dashboard}/hyperpure/orders`, 
      { params: httpParams, headers: this.entityHeaders(entityId) }
    );
  }

  getOrderByNumber(orderNumber: string, entityId?: string): Observable<OrderResponse> {
    return this.http.get<OrderResponse>(
      `${this.appGlobals.apiUrl.dashboard}/hyperpure/orders/${orderNumber}`,
      { headers: this.entityHeaders(entityId) }
    );
  }

  getInvoiceFile(orderNumber: string, invoiceType: string = 'combined', entityId?: string): Observable<Blob> {
    return this.http.get(
      `${this.appGlobals.apiUrl.dashboard}/hyperpure/orders/${orderNumber}/invoice/${invoiceType}/file`,
      { responseType: 'blob', headers: this.entityHeaders(entityId) }
    );
  }

  getInvoiceSignedUrl(orderNumber: string, invoiceType: string = 'combined', download: boolean = false, entityId?: string): Observable<InvoiceUrlResponse> {
    let params = new HttpParams();
    if (download) {
      params = params.set('download', 'true');
    }
    return this.http.get<InvoiceUrlResponse>(
      `${this.appGlobals.apiUrl.dashboard}/hyperpure/orders/${orderNumber}/invoice/${invoiceType}`,
      { params, headers: this.entityHeaders(entityId) }
    );
  }

  downloadInvoice(orderNumber: string, invoiceType: string = 'combined', entityId?: string): void {
    this.getInvoiceSignedUrl(orderNumber, invoiceType, true, entityId).subscribe({
      next: (response) => {
        if (response.success && response.data.signedUrl) {
          const link = document.createElement('a');
          link.href = response.data.signedUrl;
          link.download = response.data.fileName || `invoice-${orderNumber}.pdf`;
          link.target = '_blank';
          link.rel = 'noopener';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      },
      error: (error) => {
        console.error('Error downloading invoice:', error);
      }
    });
  }
}
